import m from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Icon, Tippy} from '@bitstillery/common/components'
import {watch, type Signal, type Unwatch} from '@bitstillery/mithril'
import type {TippyAttrs} from '@bitstillery/common/components/ui/tip/tip'

interface StepperOption {
    _disabled?: boolean | number
    icon: string
    link?: string | ((step: number) => string)
    title: TippyAttrs['content']
    warning?: boolean
}

interface StepperAttrs {
    model: {$selection: Signal<number>; _direction?: string; direction?: string; selection: number}
    onChange?: (step: number, selection: number) => void
    options: readonly StepperOption[]
    size?: number
    tipPlacement?: TippyAttrs['placement']
}

export class Stepper extends MithrilComponent<StepperAttrs> {
    watchers: Unwatch[]

    constructor(vn: m.Vnode<StepperAttrs>) {
        super()
        this.watchers = [
            watch(vn.attrs.model.$selection, (i) => {
                // Sync step changes with the Stepper's onChange handler.
                if (vn.attrs.onChange) {
                    vn.attrs.onChange(i, vn.attrs.model.selection)
                }
            }),
        ]
    }

    override onremove() {
        this.watchers.map((unwatch) => unwatch())
    }

    view(vn: m.Vnode<StepperAttrs>) {
        return (
            <div
                className={classes('c-stepper', {
                    completed: vn.attrs.model.selection >= vn.attrs.options.length - 1,
                    [(vn.attrs.model._direction ?? vn.attrs.model.direction) || 'horizontal']: true,
                    [vn.attrs.size !== undefined && vn.attrs.size > 0 ? `size-${vn.attrs.size}` : 'size-d']: true,
                })}
                // The connecting line masks itself open at every step centre (see stepper.scss);
                // that only works if the stylesheet knows how many steps share the track.
                style={{'--step-count': String(Math.max(vn.attrs.options.length, 2))}}
            >
                <div className='steps'>
                    <div className='progress-line' />
                    {vn.attrs.options.map((option, i) => {
                        const disabled = option._disabled && vn.attrs.model.selection !== i
                        return (
                            <Tippy
                                className={classes('step', {
                                    active: vn.attrs.model.selection === i,
                                    completed: vn.attrs.model.selection >= i + 1,
                                    disabled,
                                    warning: option.warning ? option.warning : false,
                                })}
                                content={option.title}
                                onclick={() => {
                                    if (disabled) {
                                        return
                                    }
                                    if (!option._disabled) {
                                        vn.attrs.model.selection = i
                                    }
                                }}
                                placement={vn.attrs.tipPlacement}
                            >
                                {(() => {
                                    if (option.link) {
                                        return (
                                            <a
                                                className={'step-link'}
                                                href={typeof option.link === 'function' ? option.link(i) : option.link}
                                                onclick={(e: MouseEvent) => {
                                                    e.preventDefault()
                                                    if (disabled) {
                                                        return
                                                    }

                                                    const link = typeof option.link === 'function' ? option.link(i) : option.link
                                                    void m.route.set(String(link))
                                                    if (vn.attrs.onChange) {
                                                        vn.attrs.onChange(i, vn.attrs.model.selection)
                                                    }
                                                }}
                                            >
                                                <Icon name={option.icon} type='unset' />
                                            </a>
                                        )
                                    }
                                    return <Icon name={option.icon} type='unset' />
                                })()}
                            </Tippy>
                        )
                    })}
                </div>
            </div>
        )
    }
}
